import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useI18n } from "@/i18n";
import { useAuth } from "@/auth";
import { endpoints } from "@/api";
import { Library, GraduationCap, ArrowRight, CheckCircle2 } from "lucide-react";

// Compact "Continue where you left off" card shown on the homepage to
// logged-in users only. Surfaces the next un-completed Encyclopedia module
// and Academy chapter with deep links straight into the right item.
export default function ContinueLearning() {
  const { isRTL } = useI18n();
  const { user } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ["progress-next", user?.user_id],
    queryFn: () => endpoints.progressNext(),
    enabled: !!user,
    staleTime: 30_000,
  });

  if (!user || isLoading || !data) return null;

  const enc = data.encyclopedia || {};
  const acad = data.academy || {};
  // Hide entire card if user is brand new (no progress) AND catalog is empty (avoid empty render).
  if (!enc.total && !acad.total) return null;

  const serifClass = isRTL ? "font-serif-ar" : "font-serif-en";
  const arrow = isRTL ? "rotate-180" : "";

  return (
    <section
      className="max-w-7xl mx-auto px-6 lg:px-10 mt-16"
      data-testid="continue-learning-section"
    >
      <div className="flex items-end justify-between mb-5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#C86240] mb-2">
            {isRTL ? "تابع رحلتك" : "Pick up where you left off"}
          </div>
          <h2 className={`${serifClass} text-2xl sm:text-3xl text-[#2A2421]`}>
            {isRTL ? "كمّل من وين وقفت" : "Continue learning"}
          </h2>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <ContinueCard
          isRTL={isRTL}
          icon={Library}
          eyebrow={isRTL ? "الموسوعة" : "Encyclopedia"}
          stats={`${enc.completed || 0}/${enc.total || 0}`}
          done={!!enc.done}
          next={enc.next}
          toIfNext={(n) => `/encyclopedia?m=${encodeURIComponent(n.id)}`}
          toIfDone="/encyclopedia"
          ctaContinueAr="تابع القراءة"
          ctaContinueEn="Continue reading"
          ctaDoneAr="راجع الموسوعة"
          ctaDoneEn="Review all modules"
          doneLabelAr="أكملت كل الموديولات"
          doneLabelEn="All modules completed"
          startLabelAr="ابدأ"
          startLabelEn="Get started"
          arrow={arrow}
          testid="continue-card-enc"
        />
        <ContinueCard
          isRTL={isRTL}
          icon={GraduationCap}
          eyebrow={isRTL ? "الأكاديمي" : "Academy"}
          stats={`${acad.completed || 0}/${acad.total || 0}`}
          done={!!acad.done}
          next={acad.next}
          toIfNext={(n) =>
            `/academy?t=${encodeURIComponent(n.track_id || "")}&c=${encodeURIComponent(n.chapter_id)}`
          }
          toIfDone="/academy"
          ctaContinueAr="تابع الفصل"
          ctaContinueEn="Continue chapter"
          ctaDoneAr="راجع الأكاديمي"
          ctaDoneEn="Review the academy"
          doneLabelAr="تخرّجت من الأكاديمي"
          doneLabelEn="Academy graduated"
          startLabelAr="ابدأ"
          startLabelEn="Get started"
          arrow={arrow}
          testid="continue-card-academy"
        />
      </div>
    </section>
  );
}

function ContinueCard({
  isRTL,
  icon: Icon,
  eyebrow,
  stats,
  done,
  next,
  toIfNext,
  toIfDone,
  ctaContinueAr,
  ctaContinueEn,
  ctaDoneAr,
  ctaDoneEn,
  doneLabelAr,
  doneLabelEn,
  startLabelAr,
  startLabelEn,
  arrow,
  testid,
}) {
  const hasNext = !!next?.id || !!next?.chapter_id;
  const to = hasNext ? toIfNext(next) : toIfDone;

  let title = isRTL
    ? (next?.title_ar || (done ? doneLabelAr : startLabelAr))
    : (next?.title_en || (done ? doneLabelEn : startLabelEn));

  const cta = hasNext
    ? (isRTL ? ctaContinueAr : ctaContinueEn)
    : (isRTL ? ctaDoneAr : ctaDoneEn);

  // For academy "next" rows, show the parent track as the eyebrow tail
  const trackLine = next?.track_id
    ? (isRTL ? next.track_title_ar : next.track_title_en)
    : null;

  return (
    <Link
      to={to}
      data-testid={testid}
      className="group rounded-2xl bg-white border border-[#2A2421]/10 p-5 hover:border-[#C86240]/40 hover:-translate-y-0.5 transition-all flex items-center gap-4"
    >
      <div className="shrink-0 w-12 h-12 rounded-xl bg-[#C86240]/10 text-[#C86240] flex items-center justify-center group-hover:bg-[#C86240] group-hover:text-white transition-colors">
        {done ? <CheckCircle2 className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#5C544F] mb-1">
          <span>{eyebrow}</span>
          <span className="text-[#2A2421]/30">·</span>
          <span className="tabular-nums">{stats}</span>
          {done && (
            <span className="ms-1 text-[#4A9966]">
              {isRTL ? "مكتمل" : "Done"}
            </span>
          )}
        </div>
        <div className="text-sm font-semibold text-[#2A2421] leading-tight line-clamp-2">
          {title}
        </div>
        {trackLine && (
          <div className="text-xs text-[#5C544F] mt-1 truncate">{trackLine}</div>
        )}
        <div className="mt-2 inline-flex items-center gap-1.5 text-xs text-[#C86240] font-medium">
          <span>{cta}</span>
          <ArrowRight className={`w-3.5 h-3.5 transition-transform group-hover:translate-x-1 ${arrow}`} />
        </div>
      </div>
    </Link>
  );
}
